<template>
  <component :is="as" :class="['view', ...classList]" :style="styles">
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { ViewProps } from './view-props.interface';

defineOptions({
  inheritAttrs: true,
});

const props = withDefaults(defineProps<ViewProps>(), {
  as: 'div',
  direction: 'row',
});

const styles = computed(() => {
  const styleList: { flexShrink?: number; flexGrow?: number } = {};

  if (props.shrink !== undefined) {
    styleList.flexShrink = props.shrink;
  }

  if (props.grow !== undefined) {
    styleList.flexGrow = props.grow;
  }

  return styleList;
});

const classList = computed(() => [
  `direction-${props.direction}`,
  props.wrap && props.wrap,
  props.gap && `gap-${props.gap}`,
  props.rowGap && `row-gap-${props.rowGap}`,
  props.columnGap && `column-gap-${props.columnGap}`,
  props.padding && `padding-${props.padding}`,
  props.paddingInline && `padding-inline-${props.paddingInline}`,
  props.paddingBlock && `padding-block-${props.paddingBlock}`,
  props.paddingTop && `padding-top-${props.paddingTop}`,
  props.paddingBottom && `padding-bottom-${props.paddingBottom}`,
  props.paddingStart && `padding-start-${props.paddingStart}`,
  props.paddingEnd && `padding-end-${props.paddingEnd}`,
  props.align && `alignment-${props.align}`,
  props.justify && `justify-${props.justify}`,
]);
</script>

<style lang="scss" scoped>
$spacings: (
  'xs': --t-spacing-xs,
  'sm': --t-spacing-sm,
  'md': --t-spacing-md,
  'lg': --t-spacing-lg,
  'xl': --t-spacing-xl,
);
$flex-directions: row, column, row-reverse, column-reverse;
$flex-wrap: wrap, nowrap, wrap-reverse;
$flex-alignments: start, center, end, normal, stretch, baseline;
$flex-justify: center, start, end, space-between, space-around;

.view {
  display: flex;
}

@each $direction in $flex-directions {
  .direction-#{$direction} {
    flex-direction: $direction;
  }
}

@each $wrap in $flex-wrap {
  .#{$wrap} {
    flex-wrap: $wrap;
  }
}

@each $alignment in $flex-alignments {
  .alignment-#{$alignment} {
    align-items: $alignment;
  }
}

@each $justify in $flex-justify {
  .justify-#{$justify} {
    justify-content: $justify;
  }
}

@each $spacing, $variable in $spacings {
  .gap-#{$spacing} {
    gap: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .row-gap-#{$spacing} {
    row-gap: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .column-gap-#{$spacing} {
    column-gap: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-#{$spacing} {
    padding: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-inline-#{$spacing} {
    padding-inline: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-block-#{$spacing} {
    padding-block: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-top-#{$spacing} {
    padding-top: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-bottom-#{$spacing} {
    padding-bottom: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-start-#{$spacing} {
    padding-left: var($variable);
  }
}

@each $spacing, $variable in $spacings {
  .padding-end-#{$spacing} {
    padding-right: var($variable);
  }
}
</style>
